import { Text, Button, VStack } from "@chakra-ui/react";
import { ProblemCode } from "@syla/shared/errors/ProblemCode";
import { isSylaApiError } from "@syla/shared/errors/SylaApiError";
import React, { useState, useContext } from "react";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { UserContext } from "../../../contexts/UserContext";
import { useNavigator, Route } from "../../../routers/navigator";
import { FormContainer } from "../../atoms/Containers";
import {
  PasswordRequirements,
  validatePasswordInput,
} from "../../atoms/PasswordRequirements";
import { UnexpectedError } from "../../atoms/UnexpectedError";
import { InputField } from "../../forms/InputField";
import { SylaFormHeader } from "../forms/SylaFormHeader";

type ResetPasswordFormValues = {
  password: string;
  confirmPassword: string;
};

export const ResetPasswordForm = (): JSX.Element => {
  const { resetPassword } = useContext(UserContext);

  const location = useLocation();
  const navigate = useNavigator();
  const token = new URLSearchParams(location.search).get("token");

  const form = useForm<ResetPasswordFormValues>();

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = form;

  const [error, setError] = useState<unknown | undefined>();

  async function onSubmit({
    password,
  }: ResetPasswordFormValues): Promise<void> {
    setError(undefined);
    try {
      const { defaultAccountId } = await resetPassword(token, password);
      navigate({
        route: Route.Home,
        accountId: defaultAccountId,
        replace: true,
      });
    } catch (error) {
      setError(error);
    }
  }

  function renderError(error: unknown): JSX.Element {
    if (isSylaApiError(error)) {
      if (error.code === ProblemCode.ExpiredLink) {
        return (
          <Text color="red.500">
            {"This reset link is no-longer valid. Please "}
            <Button
              color="red.500"
              disabled={false}
              onClick={() =>
                navigate({
                  route: Route.ForgotPassword,
                })
              }
              variant="link"
            >
              try again.
            </Button>
          </Text>
        );
      }
    }

    return <UnexpectedError />;
  }

  return (
    <FormContainer>
      <SylaFormHeader heading="Set your new password" />

      <VStack
        as="form"
        align="stretch"
        spacing={6}
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <InputField
          form={form}
          name="password"
          label="New password"
          type="password"
          placeholder="Type your new password"
          autoComplete="new-password"
          isRequired
          helperText={<PasswordRequirements />}
          rules={{
            validate: validatePasswordInput,
          }}
          autoFocus
        />

        <InputField
          form={form}
          name="confirmPassword"
          label="Confirm your password"
          type="password"
          placeholder="Type your new password again"
          autoComplete="new-password"
          isRequired
          rules={{
            validate: (confirmPassword?: string) =>
              confirmPassword === form.getValues("password") ?
                undefined
              : "Password does not match",
          }}
        />

        <Button
          colorScheme="primary"
          isDisabled={isSubmitting}
          isLoading={isSubmitting}
          width="full"
          type="submit"
        >
          Continue
        </Button>
        {error && renderError(error)}
      </VStack>
    </FormContainer>
  );
};
